import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { api } from '../../services/api';
import {
  Mail, Phone, User, Calendar, CheckCircle, Circle, MessageSquare,
  Search, Filter, ChevronDown, ChevronUp, Clock, Eye, Download, Upload, X, Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';

interface ContactLead {
  id: number;
  name: string;
  email: string | null;
  mobile_number: string;
  subject: string;
  message: string;
  is_read: number;
  created_at: string;
  sender_user_id: number | null;
  sender_name: string | null;
  sender_email: string | null;
}

type LeadStatus = 'contacted' | 'follow_up' | 'converted' | 'lost';

// Get stored status from localStorage
const getStoredStatus = (leadId: number): LeadStatus | null => {
  const stored = localStorage.getItem(`lead_status_${leadId}`);
  if (stored === 'contacted' || stored === 'follow_up' || stored === 'converted' || stored === 'lost') {
    return stored;
  }
  return null;
};

// Set status in localStorage
const setStoredStatus = (leadId: number, status: LeadStatus | null): void => {
  if (status) {
    localStorage.setItem(`lead_status_${leadId}`, status);
  } else {
    localStorage.removeItem(`lead_status_${leadId}`);
  }
};

// Determine lead status (returns null if unset)
const getLeadStatus = (lead: ContactLead): LeadStatus | null => {
  return getStoredStatus(lead.id);
};

const statusConfig: Record<string, { label: string; color: string; icon: typeof Circle; iconColor: string }> = {
  contacted: {
    label: 'Contacted',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: Phone,
    iconColor: 'text-blue-500'
  },
  follow_up: {
    label: 'Follow-up',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    icon: Clock,
    iconColor: 'text-yellow-500'
  },
  converted: {
    label: 'Converted',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle,
    iconColor: 'text-green-500'
  },
  lost: {
    label: 'Lost',
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: Circle,
    iconColor: 'text-red-500'
  }
};

const unsetStatusConfig = {
  label: 'Unset',
  color: 'bg-secondary-100 text-secondary-700 border-secondary-200',
  icon: Circle,
  iconColor: 'text-secondary-400'
};

// --- CSV helpers ---

/** Parse a single CSV line handling quoted fields */
function parseCSVLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++; // skip escaped quote
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        fields.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
  }
  fields.push(current);
  return fields;
}

/** Parse full CSV text into rows of string arrays */
function parseCSV(text: string): string[][] {
  // Normalise line endings
  const normalised = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalised.split('\n').filter(l => l.trim() !== '');
  return lines.map(parseCSVLine);
}

/** Escape a value for CSV output */
function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
}

// Lead field definitions for import mapping
const LEAD_IMPORT_FIELDS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'email', label: 'Email', required: false },
  { key: 'mobile_number', label: 'Mobile Number', required: true },
  { key: 'subject', label: 'Subject', required: true },
  { key: 'message', label: 'Message', required: true },
] as const;

// --- Import Modal Component ---

interface ImportModalProps {
  csvHeaders: string[];
  csvRows: string[][];
  onConfirm: (mapping: Record<string, number>) => void;
  onCancel: () => void;
  importing: boolean;
}

const ImportModal: React.FC<ImportModalProps> = ({ csvHeaders, csvRows, onConfirm, onCancel, importing }) => {
  // Auto-guess column mapping based on header similarity
  const guessMapping = useCallback((): Record<string, number> => {
    const mapping: Record<string, number> = {};
    const headerLower = csvHeaders.map(h => h.toLowerCase().trim());

    for (const field of LEAD_IMPORT_FIELDS) {
      const key = field.key;
      // Try exact match
      let idx = headerLower.indexOf(key);
      if (idx === -1) {
        // Try label match
        idx = headerLower.indexOf(field.label.toLowerCase());
      }
      if (idx === -1) {
        // Try partial matches
        if (key === 'mobile_number') {
          idx = headerLower.findIndex(h => h.includes('mobile') || h.includes('phone') || h.includes('tel'));
        } else if (key === 'name') {
          idx = headerLower.findIndex(h => h.includes('name') && !h.includes('email'));
        } else if (key === 'email') {
          idx = headerLower.findIndex(h => h.includes('email') || h.includes('mail'));
        } else if (key === 'subject') {
          idx = headerLower.findIndex(h => h.includes('subject') || h.includes('topic'));
        } else if (key === 'message') {
          idx = headerLower.findIndex(h => h.includes('message') || h.includes('body') || h.includes('content'));
        }
      }
      mapping[key] = idx >= 0 ? idx : -1;
    }
    return mapping;
  }, [csvHeaders]);

  const [mapping, setMapping] = useState<Record<string, number>>(guessMapping);

  const previewRows = csvRows.slice(0, 5);

  const handleMappingChange = (fieldKey: string, colIndex: number) => {
    setMapping(prev => ({ ...prev, [fieldKey]: colIndex }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200">
          <h2 className="text-lg font-semibold text-secondary-900">Import CSV - Map Columns</h2>
          <button onClick={onCancel} className="text-secondary-500 hover:text-secondary-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Column Mapping */}
          <div>
            <h3 className="text-sm font-medium text-secondary-700 mb-3">Map CSV columns to lead fields</h3>
            <div className="space-y-3">
              {LEAD_IMPORT_FIELDS.map(field => (
                <div key={field.key} className="flex items-center gap-4">
                  <label className="w-40 text-sm font-medium text-secondary-700 flex-shrink-0">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <select
                    value={mapping[field.key] ?? -1}
                    onChange={(e) => handleMappingChange(field.key, parseInt(e.target.value, 10))}
                    className="flex-1 px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-sm"
                  >
                    <option value={-1}>-- Don&apos;t import --</option>
                    {csvHeaders.map((header, i) => (
                      <option key={i} value={i}>{header || `Column ${i + 1}`}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div>
            <h3 className="text-sm font-medium text-secondary-700 mb-3">
              Preview (first {previewRows.length} of {csvRows.length} rows)
            </h3>
            <div className="overflow-x-auto border border-secondary-200 rounded-lg">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-secondary-50">
                    {LEAD_IMPORT_FIELDS.map(field => (
                      <th key={field.key} className="px-3 py-2 text-left font-medium text-secondary-600">
                        {field.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, ri) => (
                    <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-secondary-50'}>
                      {LEAD_IMPORT_FIELDS.map(field => {
                        const colIdx = mapping[field.key] ?? -1;
                        const value = colIdx >= 0 && colIdx < row.length ? row[colIdx] : '';
                        return (
                          <td key={field.key} className="px-3 py-2 text-secondary-700 truncate max-w-[200px]">
                            {value || <span className="text-secondary-300 italic">empty</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-secondary-200">
          <p className="text-sm text-secondary-500">{csvRows.length} rows to import</p>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={importing}
              className="px-4 py-2 text-sm font-medium text-secondary-700 bg-secondary-100 hover:bg-secondary-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(mapping)}
              disabled={importing}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Import {csvRows.length} Rows
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Leads Component ---

const Leads: React.FC = () => {
  const [leads, setLeads] = useState<ContactLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLeadId, setExpandedLeadId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'unset' | LeadStatus>('all');
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [leadStatuses, setLeadStatuses] = useState<Record<number, LeadStatus | null>>({});

  // Selection state for export
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Import state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importHeaders, setImportHeaders] = useState<string[]>([]);
  const [importRows, setImportRows] = useState<string[][]>([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  // Initialize lead statuses after fetching
  useEffect(() => {
    const statuses: Record<number, LeadStatus | null> = {};
    leads.forEach(lead => {
      statuses[lead.id] = getLeadStatus(lead);
    });
    setLeadStatuses(statuses);
  }, [leads]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contact-leads');
      if (response.data.success) {
        setLeads(response.data.leads || []);
      } else {
        toast.error('Failed to load leads');
      }
    } catch (error: any) {
      console.error('Failed to fetch leads:', error);
      toast.error(error.response?.data?.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = (leadId: number, newStatus: LeadStatus) => {
    setStoredStatus(leadId, newStatus);
    setLeadStatuses(prev => ({ ...prev, [leadId]: newStatus }));
    const config = statusConfig[newStatus];
    toast.success(`Status updated to ${config?.label || newStatus}`);
  };

  const handleDeleteLead = async (leadId: number, leadName: string) => {
    if (!window.confirm(`Delete lead "${leadName}"? This cannot be undone.`)) return;
    try {
      const response = await api.delete(`/contact-leads/${leadId}`);
      if (response.data.success) {
        setLeads(prev => prev.filter(l => l.id !== leadId));
        setSelectedIds(prev => {
          const next = new Set(prev);
          next.delete(leadId);
          return next;
        });
        setStoredStatus(leadId, null);
        setLeadStatuses(prev => {
          const next = { ...prev };
          delete next[leadId];
          return next;
        });
        if (expandedLeadId === leadId) setExpandedLeadId(null);
        toast.success('Lead deleted');
      } else {
        toast.error(response.data.message || 'Failed to delete lead');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete lead');
    }
  };

  // Get unique subjects for filter dropdown
  const uniqueSubjects = useMemo(() => {
    const subjects = new Set(leads.map(lead => lead.subject));
    return Array.from(subjects).sort();
  }, [leads]);

  // Load predefined subjects from localStorage
  const predefinedSubjects = useMemo(() => {
    try {
      const stored = localStorage.getItem('contact_form_subjects');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  // Combine unique subjects with predefined subjects for filter
  const allSubjects = useMemo(() => {
    const combined = new Set([...uniqueSubjects, ...predefinedSubjects]);
    return Array.from(combined).sort();
  }, [uniqueSubjects, predefinedSubjects]);

  // Filter leads based on search and filters
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          lead.name.toLowerCase().includes(query) ||
          (lead.email?.toLowerCase().includes(query)) ||
          lead.subject.toLowerCase().includes(query) ||
          lead.message.toLowerCase().includes(query) ||
          lead.mobile_number.includes(query);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        const currentStatus = leadStatuses[lead.id] ?? getLeadStatus(lead);
        if (statusFilter === 'unset') {
          if (currentStatus !== null) return false;
        } else {
          if (currentStatus !== statusFilter) return false;
        }
      }

      // Subject filter
      if (subjectFilter !== 'all' && lead.subject !== subjectFilter) {
        return false;
      }

      return true;
    });
  }, [leads, searchQuery, statusFilter, subjectFilter, leadStatuses]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpand = (leadId: number) => {
    setExpandedLeadId(expandedLeadId === leadId ? null : leadId);
  };

  // --- Selection helpers ---

  const toggleSelectLead = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filteredIds = useMemo(() => new Set(filteredLeads.map(l => l.id)), [filteredLeads]);

  const allFilteredSelected = filteredLeads.length > 0 && filteredLeads.every(l => selectedIds.has(l.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      // Deselect all filtered
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      // Select all filtered
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  // --- Export CSV ---

  const handleExport = () => {
    const leadsToExport = selectedIds.size > 0
      ? filteredLeads.filter(l => selectedIds.has(l.id))
      : filteredLeads;

    if (leadsToExport.length === 0) {
      toast.error('No leads to export');
      return;
    }

    const headers = ['Name', 'Email', 'Mobile', 'Subject', 'Message', 'Status', 'Created At'];
    const rows = leadsToExport.map(lead => {
      const status = leadStatuses[lead.id] ?? getLeadStatus(lead);
      const statusLabel = status && statusConfig[status] ? statusConfig[status].label : '';
      return [
        csvEscape(lead.name),
        csvEscape(lead.email || ''),
        csvEscape(lead.mobile_number),
        csvEscape(lead.subject),
        csvEscape(lead.message),
        csvEscape(statusLabel),
        csvEscape(formatDate(lead.created_at)),
      ].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `leads-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${leadsToExport.length} leads`);
  };

  // --- Import CSV ---

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      if (!text) {
        toast.error('Could not read file');
        return;
      }

      const allRows = parseCSV(text);
      if (allRows.length < 2) {
        toast.error('CSV must have a header row and at least one data row');
        return;
      }

      setImportHeaders(allRows[0]);
      setImportRows(allRows.slice(1));
      setImportModalOpen(true);
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsText(file);

    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  const handleImportConfirm = async (mapping: Record<string, number>) => {
    // Validate that required fields are mapped
    for (const field of LEAD_IMPORT_FIELDS) {
      if (field.required && (mapping[field.key] === undefined || mapping[field.key] < 0)) {
        toast.error(`"${field.label}" is required. Please map it to a CSV column.`);
        return;
      }
    }

    setImporting(true);
    try {
      const response = await api.post('/contact-leads/import', {
        rows: importRows,
        columnMapping: mapping,
      });

      if (response.data.success) {
        const { imported, skipped } = response.data;
        toast.success(`Imported ${imported} leads${skipped > 0 ? ` (${skipped} skipped)` : ''}`);
        setImportModalOpen(false);
        setImportHeaders([]);
        setImportRows([]);
        fetchLeads(); // Refresh list
      } else {
        toast.error(response.data.message || 'Import failed');
      }
    } catch (error: any) {
      console.error('Import failed:', error);
      toast.error(error.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Contact Leads</h1>
          <p className="text-secondary-600">
            View and manage contact form submissions from your public profile
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Import CSV */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileSelect}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-secondary-700 bg-secondary-100 hover:bg-secondary-200 rounded-lg transition-colors"
          >
            <Upload className="h-4 w-4" />
            Import CSV
          </button>
          {/* Export CSV */}
          <button
            onClick={handleExport}
            disabled={filteredLeads.length === 0}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export CSV
            {selectedIds.size > 0 && ` (${selectedIds.size})`}
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search Bar */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
            <input
              type="text"
              placeholder="Search by name, email, subject, message..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'unset' | LeadStatus)}
              className="appearance-none pl-4 pr-10 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="unset">Unset</option>
              <option value="contacted">Contacted</option>
              <option value="follow_up">Follow-up</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
            <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400 pointer-events-none" />
          </div>

          {/* Subject Filter */}
          <div className="relative">
            <select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white cursor-pointer min-w-[150px]"
            >
              <option value="all">All Subjects</option>
              {allSubjects.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400 pointer-events-none" />
          </div>
        </div>

        {/* Results count + Select All */}
        <div className="flex items-center gap-4">
          {filteredLeads.length > 0 && (
            <label className="flex items-center gap-2 cursor-pointer text-sm text-secondary-600">
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleSelectAll}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              Select all ({filteredLeads.length})
            </label>
          )}
          <p className="text-sm text-secondary-500">
            Showing {filteredLeads.length} of {leads.length} leads
            {selectedIds.size > 0 && ` | ${selectedIds.size} selected`}
          </p>
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="card text-center py-12">
          <MessageSquare className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-900 mb-2">No leads yet</h2>
          <p className="text-secondary-600">
            Contact form submissions will appear here once visitors start reaching out.
          </p>
        </div>
      ) : filteredLeads.length === 0 ? (
        <div className="card text-center py-12">
          <Search className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-900 mb-2">No matching leads</h2>
          <p className="text-secondary-600">
            Try adjusting your search or filter criteria.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLeads.map((lead) => {
            const status = leadStatuses[lead.id] ?? getLeadStatus(lead);
            const config = status && statusConfig[status] ? statusConfig[status] : unsetStatusConfig;
            const StatusIcon = config.icon;
            const isExpanded = expandedLeadId === lead.id;
            const isSelected = selectedIds.has(lead.id);

            const borderClass = status === 'contacted' ? 'border-l-4 border-l-blue-500'
              : status === 'follow_up' ? 'border-l-4 border-l-yellow-500'
              : status === 'converted' ? 'border-l-4 border-l-green-500'
              : status === 'lost' ? 'border-l-4 border-l-red-500'
              : 'border-l-4 border-l-secondary-300';

            return (
              <div
                key={lead.id}
                className={`card transition-all ${borderClass}`}
              >
                {/* Lead Header - Always visible */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Selection checkbox */}
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectLead(lead.id)}
                      className="mt-1.5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
                    />
                    <StatusIcon className={`h-5 w-5 flex-shrink-0 mt-1 ${config.iconColor}`} />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-secondary-900 truncate">
                        {lead.name}
                      </h3>
                      <p className="text-sm text-secondary-600 truncate">
                        {lead.subject}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-secondary-500 mt-1">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(lead.created_at)}</span>
                        </div>
                        {lead.email && (
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            <span className="truncate max-w-[200px]">{lead.email}</span>
                          </div>
                        )}
                        {lead.mobile_number && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            <span>{lead.mobile_number}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    {/* Status Dropdown */}
                    <select
                      value={status || ''}
                      onChange={(e) => updateLeadStatus(lead.id, e.target.value as LeadStatus)}
                      className={`text-xs font-medium px-3 py-1.5 rounded-full border cursor-pointer ${config.color}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!status && <option value="">-- Set status --</option>}
                      <option value="contacted">Contacted</option>
                      <option value="follow_up">Follow-up</option>
                      <option value="converted">Converted</option>
                      <option value="lost">Lost</option>
                    </select>

                    {/* View Message Button */}
                    <button
                      onClick={() => toggleExpand(lead.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-lg transition-colors text-sm font-medium"
                    >
                      <Eye className="h-4 w-4" />
                      {isExpanded ? (
                        <>
                          Hide
                          <ChevronUp className="h-4 w-4" />
                        </>
                      ) : (
                        <>
                          View Message
                          <ChevronDown className="h-4 w-4" />
                        </>
                      )}
                    </button>

                    {/* Delete Lead Button */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDeleteLead(lead.id, lead.name); }}
                      className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                      title="Delete lead"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="hidden sm:inline">Delete</span>
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="mt-6 pt-6 border-t border-secondary-200 space-y-4">
                    {/* Contact Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {lead.email && (
                        <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg">
                          <Mail className="h-5 w-5 text-secondary-600" />
                          <div>
                            <p className="text-xs text-secondary-500">Email</p>
                            <a
                              href={`mailto:${lead.email}`}
                              className="text-sm font-medium text-secondary-900 hover:text-primary-600"
                            >
                              {lead.email}
                            </a>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg">
                        <Phone className="h-5 w-5 text-secondary-600" />
                        <div>
                          <p className="text-xs text-secondary-500">Mobile Number</p>
                          <a
                            href={`tel:${lead.mobile_number}`}
                            className="text-sm font-medium text-secondary-900 hover:text-primary-600"
                          >
                            {lead.mobile_number}
                          </a>
                        </div>
                      </div>
                      {lead.sender_user_id && (
                        <div className="flex items-center gap-3 p-3 bg-secondary-50 rounded-lg">
                          <User className="h-5 w-5 text-secondary-600" />
                          <div>
                            <p className="text-xs text-secondary-500">Registered User</p>
                            <p className="text-sm font-medium text-secondary-900">
                              {lead.sender_name || 'User'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Subject */}
                    <div>
                      <h4 className="text-sm font-medium text-secondary-700 mb-2">Subject</h4>
                      <p className="text-base text-secondary-900">{lead.subject}</p>
                    </div>

                    {/* Message */}
                    <div>
                      <h4 className="text-sm font-medium text-secondary-700 mb-2">Message</h4>
                      <div className="p-4 bg-secondary-50 rounded-lg">
                        <p className="text-sm text-secondary-900 whitespace-pre-wrap">
                          {lead.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Import CSV Modal */}
      {importModalOpen && (
        <ImportModal
          csvHeaders={importHeaders}
          csvRows={importRows}
          onConfirm={handleImportConfirm}
          onCancel={() => {
            setImportModalOpen(false);
            setImportHeaders([]);
            setImportRows([]);
          }}
          importing={importing}
        />
      )}
    </div>
  );
};

export default Leads;
